<script setup lang="ts">
import { useI18n } from '#i18n';
import { XButton, XIcon } from '@clicksign/excalibur';

import { ScreenSizes } from '~~core';

import DocumentToolbarSkeleton from '../DocumentToolbarSkeleton/DocumentToolbarSkeleton.vue';

export type RubricateFooterProps = {
  hidePageCounter?: boolean;
  pagesCount?: number;
  currentPage?: number;
  loading?: boolean;
  documentRubricates?: number;
  documentTotalRubricates?: number;
  isLastDocument?: boolean;
};

export type RubricateFooterEmits = {
  (e: 'go-next'): void,
  (e: 'to-top'): void,
  (e: 'to-next-field'): void
};

const props = withDefaults(defineProps<RubricateFooterProps>(), {
  hidePageCounter: false,
  pagesCount: 1,
  currentPage: 1,
  loading: true,
  documentRubricates: 0,
  documentTotalRubricates: 1,
});

const { t } = useI18n();

const canGoNext = computed(() => props.documentRubricates === props.documentTotalRubricates);
const concludeButton = computed(() => (canGoNext.value ? t('common.conclude') : t('document.rubricate.nextField')));

const emit = defineEmits<RubricateFooterEmits>();
const handleNextButton = () => (canGoNext.value ? emit('go-next') : emit('to-next-field'));

const skeletonLength = window.innerWidth > ScreenSizes.MEDIUM ? 3 : 1;
</script>

<template>
  <aside
    v-if="!loading"
    :class="$style.toolbar"
    data-testid="rubricateFooter"
  >
    <!--
      Feature doens't work in legacy mode
      TODO: Remove 'hide' class when solving issue: https://github.com/clicksign/nova-widget/issues/351
    -->
    <span
      :class="[$style.pageCounter, hidePageCounter && $style.hide]"
      data-testid="rubricateFooterPageCounter"
    >
      {{ $t('document.rubricate.pageCounter', { currentPage, pagesCount }) }}
    </span>

    <div :class="$style.centerActions">
      <XButton
        design="link"
        :class="$style.hideInMobile"
        data-testid="rubricateFooterToTopButton"
        @click="$emit('to-top')"
      >
        <XIcon
          icon="chevron-up"
          left
        />

        <span>{{ $t('document.rubricate.top') }}</span>
      </XButton>
      <span>
        {{ $t('document.rubricate.rubricateCount', { documentRubricates, documentTotalRubricates }) }}
      </span>

      <XButton
        v-if="!canGoNext"
        design="outlined"
        :class="$style.hideInMobile"
        data-testid="rubricateFooterToNextFieldButton"
        size="sm"
        @click="$emit('to-next-field')"
      >
        <span :class="$style.backText">{{ $t('document.rubricate.nextField') }}</span>
        <XIcon
          right
          icon="arrow-right"
        />
      </XButton>
    </div>

    <XButton
      :disabled="!canGoNext"
      :class="$style.hideInMobile"
      data-testid="rubricateFooterGoNextButton"
      @click="$emit('go-next')"
    >
      <span>{{ isLastDocument ? $t('common.conclude') : $t('document.rubricate.rubricateFooter.nextDocument') }}</span>
      <XIcon
        v-if="!isLastDocument"
        right
        icon="arrow-right"
      />
    </XButton>

    <XButton
      :design="canGoNext ? '' : 'outlined'"
      :class="$style.concludeButton"
      data-testid="rubricateFooterConcludeButton"
      @click="handleNextButton"
    >
      <span>{{ concludeButton }}</span>
      <XIcon
        v-if="!canGoNext"
        right
        icon="arrow-right"
      />
    </XButton>
  </aside>

  <DocumentToolbarSkeleton
    v-else
    :class="$style.toolbar"
    :actions-length="skeletonLength"
  />
</template>

<style lang="scss" module>
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  background: var(--color-neutral-0);
}

.hideInMobile {
  display: none;
}

.rubricateInfo {
  display: flex;
  align-items: center;
}

.centerActions {
  display: flex;
  align-items: center;
  justify-content: space-around;
  flex-direction: row;
  gap: var(--space-large);
}

.concludeButton {
  display: block;
  flex: 1;
  margin-top: var(--space-regular);
}

.pageCounter {
  &.hide {
    visibility: hidden;
  }
}

@include breakpoint('small') {
  .hideInMobile {
    display: block;
  }

  .concludeButton {
    display: none;
  }
}
</style>
